import * as Sentry from '@sentry/browser';

import './alpine/search-autocomplete';
import './alpine/dressing';

import './modules/tracking-banner';

import ContactList from './modules/contact-list';
window.ContactList = ContactList;

import { improveFilamentScroll } from './utils/improve-scroll';
improveFilamentScroll();

import { fixFilamentSearchable } from './utils/fix-searchable';
fixFilamentSearchable();

import.meta.glob(['../images/**', '../fonts/**']);

// Initialize Sentry
function initSentry() {
    // Skip Sentry initialization when no DSN is configured
    const dsn = import.meta.env.VITE_SENTRY_DSN;
    if (!dsn) {
        return;
    }

    const tracesSampleRate = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) || 0;
    const replaysSessionSampleRate = Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE) || 0;
    const replaysOnErrorSampleRate = Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE) || 0;

    Sentry.init({
        environment: import.meta.env.VITE_APP_ENV,

        dsn,

        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.thirdPartyErrorFilterIntegration({
                filterKeys: ['gigsalad-laravel-frontend'],
                behaviour: 'drop-error-if-contains-third-party-frames',
            }),
        ],

        // Set `tracesSampleRate` to capture 5% of transactions for performance monitoring
        tracesSampleRate,

        // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
            'localhost',
            /^127\.0\.0\.1:\d+$/,
            /^https:\/\/gigsalad\.test/,
            /^https:\/\/gigsalad\.com/,
        ],

        // Capture Replay for 0% of all sessions, plus for 25% of sessions with an error.
        // Consumed by the replay integration that `deferSentryReplay()` registers after
        // page load rather than at init, so these look unused here but are not: drop them
        // and replay falls back to a 0 rate and silently records nothing.
        replaysSessionSampleRate,
        replaysOnErrorSampleRate,

        // beforeSend: perform geo-based dropping on the client as a best-effort early filter
        // Note: The bundler plugin removes/marks third-party frames at build time and official runtime integrations handle other cases
        beforeSend(event) {
            const country = window.GS && window.GS.geo && window.GS.geo.country ? String(window.GS.geo.country).trim().toUpperCase() : null;

            const allowedCountriesCsvRuntime = window.GS && window.GS.allowedCountries ? window.GS.allowedCountries : import.meta.env.VITE_SENTRY_ALLOWED_COUNTRIES || 'US,CA,PR';
            const allowed = String(allowedCountriesCsvRuntime)
                .split(',')
                .map((s) => s.trim().toUpperCase())
                .filter(Boolean);

            // Annotate the event to query/filter in Sentry why an event was kept/dropped
            event.tags = event.tags || {};
            if (country) {
                event.tags.client_country = country;
                event.tags.client_country_allowed = allowed.includes(country) ? 'yes' : 'no';
            } else {
                event.tags.client_country = 'unknown';
                event.tags.client_country_allowed = 'unknown';
            }

            // Only drop when we have a reliable country and it is disallowed
            if (country && !allowed.includes(country)) {
                return null;
            }

            return event;
        },
    });

    // Add user identification to Sentry
    if (window.GS && window.GS.user) {
        Sentry.setUser(window.GS.user);
    }

    deferSentryReplay(replaysSessionSampleRate, replaysOnErrorSampleRate);
}

// Matches the load timeout `utils/google-maps-loader.js` uses for the same failure mode.
const REPLAY_LOAD_TIMEOUT_MS = 10000;

/**
 * Loads session replay after the page has loaded, keeping the largest piece of the
 * Sentry bundle and its startup DOM snapshot off the critical path.
 */
function deferSentryReplay(sessionSampleRate, onErrorSampleRate) {
    // Replay records nothing when both rates are 0, so skip the chunk entirely.
    if (sessionSampleRate <= 0 && onErrorSampleRate <= 0) {
        return;
    }

    let started = false;
    let timeoutId;

    const start = () => {
        // Replay throws when constructed twice, so whichever of the `load` listener and
        // the timeout below fires first has to be the only one that gets through.
        if (started) {
            return;
        }
        started = true;

        clearTimeout(timeoutId);
        window.removeEventListener('load', start);

        // A failed chunk fetch has to stay silent in production: an unhandled rejection
        // is reported as a new Sentry error on every affected pageview. This also catches
        // anything replay's startup throws, which would otherwise vanish without a trace,
        // so make it loud in development. `import.meta.env.DEV` is a build-time constant,
        // leaving the production bundle unchanged.
        import('./utils/sentry-replay')
            .then((module) => module.startSentryReplay())
            .catch((error) => {
                // Not an event: breadcrumbs ride along on whatever error is captured
                // later in this pageview, so this costs no extra Sentry event but keeps
                // a failed replay load from being invisible after the fact.
                Sentry.addBreadcrumb({
                    category: 'sentry.replay',
                    level: 'warning',
                    message: 'Session replay failed to start',
                    data: { error: error?.message },
                });

                if (import.meta.env.DEV) {
                    console.error('Sentry session replay failed to start', error);
                }
            });
    };

    if (document.readyState === 'complete') {
        start();
        return;
    }

    window.addEventListener('load', start, { once: true });

    // `load` waits on every subresource, so a hung script or stalled request can hold it
    // open long past the point the page is usable. Start anyway rather than record nothing
    // for exactly the slow sessions replay is most useful on.
    timeoutId = setTimeout(start, REPLAY_LOAD_TIMEOUT_MS);
}

initSentry();
